import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Box, Button, Card, Container, IconButton, Stack, styled, Typography } from '@mui/material';
import { drawerWidth } from 'constants/chat';
import MenuIcon from '@mui/icons-material/Menu';
import AddIcon from '@mui/icons-material/Add';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { AgentModal } from 'components/AgentModal/AgentModal';
import { Chat } from 'components/Chat/Chat';
import { ChatList } from 'components/ChatList/ChatList';

const Main = styled(Stack, { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme }) => ({
  flexGrow: 1,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      },
    },
  ],
}));

export const ChatPage = () => {
  const [open, setOpen] = useState(false);
  const [isNewChat, setIsNewChat] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);
  const [agent, setAgent] = useState(localStorage.getItem('agent'));
  const [isOpenAgentModal, setIsOpenAgentModal] = useState(!agent);
  const location = useLocation();

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleAgentModal = () => {
    setIsOpenAgentModal(!isOpenAgentModal);
  };

  const newChat = () => {
    setIsNewChat(true);
    setActiveChatId(null);
  };

  useEffect(() => {
    setAgent(localStorage.getItem('agent'));
  }, [location]);

  useEffect(() => {
    newChat();
  }, [agent]);

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        {/* Page Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2C3E50' }}>
              Chat
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Interact with your AI agents
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton
              onClick={handleDrawerOpen}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <MenuIcon sx={{ color: '#6B7280' }} />
            </IconButton>
            <IconButton
              onClick={newChat}
              sx={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <AddIcon sx={{ color: '#6B7280' }} />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<SwapHorizIcon />}
              onClick={handleAgentModal}
              sx={{
                backgroundColor: '#0099CC',
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: '#007AA3',
                },
              }}
            >
              Change Agent
            </Button>
          </Stack>
        </Stack>

        {/* Chat Content */}
        <Card
          sx={{
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 153, 204, 0.1)',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            sx={{
              position: 'relative',
              minHeight: '70vh',
            }}
          >
            <ChatList
              open={open}
              setActiveChatId={setActiveChatId}
              activeChatId={activeChatId}
              agentId={agent}
            />
            <Main
              direction="row"
              open={open}
              sx={{ backgroundColor: '#FFFFFF' }}
            >
              <Chat
                agentId={agent}
                isNewChat={isNewChat}
                setIsNewChat={setIsNewChat}
                id={activeChatId}
                setActiveChatId={setActiveChatId}
              />
            </Main>
          </Stack>
        </Card>
      </Stack>
      <AgentModal
        setAgent={setAgent}
        onClose={handleAgentModal}
        isOpen={isOpenAgentModal}
        agentId={agent}
      />
    </Container>
  );
};
